import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { API } from '../config';
import {
    CalendarIcon,
    UserIcon,
    ArrowLeftIcon,
    ShareIcon,
    ArrowPathIcon,
    ClockIcon,
    HeartIcon as HeartOutline,
    ChatBubbleLeftIcon,
    TrashIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolid } from '@heroicons/react/24/solid';
import { toast } from 'react-hot-toast';
import { useAppContext } from '../context/AppContext';

const BlogPostDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAppContext();
    const [blog, setBlog] = useState(null);
    const [loading, setLoading] = useState(true);
    const [commentText, setCommentText] = useState('');
    const [isLiking, setIsLiking] = useState(false);
    const [isSubmittingComment, setIsSubmittingComment] = useState(false);

    useEffect(() => {
        fetchBlogDetail();
    }, [id]);

    const fetchBlogDetail = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API}/blogs/${id}`);
            setBlog(response.data);
            window.scrollTo(0, 0);
        } catch (error) {
            console.error('Failed to fetch blog detail:', error);
            toast.error('Failed to load blog post');
            navigate('/blog');
        } finally {
            setLoading(false);
        }
    };

    const handleLike = async () => {
        if (!user) {
            toast.error('Please sign in to like this post');
            navigate('/login');
            return;
        }

        try {
            setIsLiking(true);
            const token = localStorage.getItem('token');
            const response = await axios.post(`${API}/blogs/${id}/like`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setBlog(prev => ({
                ...prev,
                likes: response.data.liked
                    ? [...(prev.likes || []), user.id]
                    : (prev.likes || []).filter(uid => uid !== user.id)
            }));
        } catch (error) {
            toast.error('Failed to update like');
        } finally {
            setIsLiking(false);
        }
    };

    const handleCommentSubmit = async (e) => {
        e.preventDefault();
        if (!user) {
            toast.error('Please sign in to comment');
            navigate('/login');
            return;
        }
        if (!commentText.trim()) return;

        try {
            setIsSubmittingComment(true);
            const token = localStorage.getItem('token');
            const response = await axios.post(`${API}/blogs/${id}/comments`,
                { comment_text: commentText },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setBlog(prev => ({
                ...prev,
                comments: [...(prev.comments || []), response.data]
            }));
            setCommentText('');
            toast.success('Comment added!');
        } catch (error) {
            toast.error('Failed to add comment');
        } finally {
            setIsSubmittingComment(false);
        }
    };

    const handleDeleteComment = async (commentId) => {
        if (!window.confirm('Delete this comment?')) return;
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${API}/blogs/${id}/comments/${commentId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setBlog(prev => ({
                ...prev,
                comments: prev.comments.filter(c => c.id !== commentId)
            }));
            toast.success('Comment deleted');
        } catch (error) {
            toast.error('Failed to delete comment');
        }
    };

    const handleShare = () => {
        if (navigator.share) {
            navigator.share({
                title: blog.title,
                text: blog.excerpt,
                url: window.location.href,
            }).catch((error) => console.log('Error sharing', error));
        } else {
            navigator.clipboard.writeText(window.location.href);
            toast.success('Link copied to clipboard!');
        }
    };

    // Calculate reading time
    const calculateReadTime = (text) => {
        if (!text) return '1 min read';
        const words = text.trim().split(/\s+/).length;
        const time = Math.ceil(words / 200);
        return `${time} min read`;
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center pt-20">
                <ArrowPathIcon className="h-10 w-10 text-[#E31E24] animate-spin" />
            </div>
        );
    }

    if (!blog) return null;

    const hasLiked = user && blog.likes?.includes(user.id);

    return (
        <div className="min-h-screen bg-white pt-32 pb-20">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Back Button */}
                <motion.button
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    onClick={() => navigate('/blog')}
                    className="flex items-center text-gray-500 hover:text-[#E31E24] mb-12 font-medium transition-colors group"
                >
                    <ArrowLeftIcon className="h-5 w-5 mr-2 transition-transform group-hover:-translate-x-1" />
                    Back to Feed
                </motion.button>

                {/* Categories/Tags */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex flex-wrap gap-2 mb-8"
                >
                    {blog.tags.map((tag, i) => (
                        <span key={i} className="bg-red-50 text-[#E31E24] text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-wider">
                            {tag}
                        </span>
                    ))}
                </motion.div>

                {/* Article Header */}
                <header className="mb-12">
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-4xl md:text-5xl font-bold text-gray-900 mb-8 leading-tight"
                    >
                        {blog.title}
                    </motion.h1>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.2 }}
                        className="flex flex-wrap items-center gap-6 py-6 border-y border-gray-100 text-gray-500 font-medium"
                    >
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center text-[#E31E24] font-bold">
                                {blog.author.charAt(0)}
                            </div>
                            <span>{blog.author}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <CalendarIcon className="h-5 w-5 text-gray-400" />
                            <span>{new Date(blog.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <ClockIcon className="h-5 w-5 text-gray-400" />
                            <span>{calculateReadTime(blog.content)}</span>
                        </div>

                        <div className="flex items-center gap-4 ml-auto">
                            <button
                                onClick={handleLike}
                                disabled={isLiking}
                                className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${hasLiked ? 'text-[#E31E24] bg-red-50' : 'text-gray-500 hover:bg-gray-50'
                                    }`}
                            >
                                {hasLiked ? <HeartSolid className="h-6 w-6" /> : <HeartOutline className="h-6 w-6" />}
                                <span className="font-bold">{blog.likes?.length || 0}</span>
                            </button>

                            <button
                                onClick={handleShare}
                                className="flex items-center gap-2 text-gray-500 hover:bg-gray-50 px-4 py-2 rounded-xl transition-all"
                            >
                                <ShareIcon className="h-5 w-5" />
                            </button>
                        </div>
                    </motion.div>
                </header>

                {/* Featured Image */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.3 }}
                    className="mb-16 rounded-[2.5rem] overflow-hidden shadow-2xl"
                >
                    <img
                        src={blog.image_url || 'https://images.unsplash.com/photo-1490645935967-10de6ba17061?auto=format&fit=crop&q=80'}
                        alt={blog.title}
                        className="w-full h-auto"
                    />
                </motion.div>

                {/* Article Content */}
                <article className="prose prose-lg prose-red max-w-none mb-20">
                    <div
                        className="text-gray-700 leading-relaxed space-y-8 text-xl"
                        dangerouslySetInnerHTML={{
                            __html: blog.content.replace(/(?:\r\n|\r|\n)/g, '<br />')
                        }}
                    />
                </article>

                {/* Comments Section */}
                <section className="mt-20 pt-12 border-t border-gray-100">
                    <div className="flex items-center gap-3 mb-10">
                        <ChatBubbleLeftIcon className="h-8 w-8 text-[#E31E24]" />
                        <h3 className="text-2xl font-bold text-gray-900">
                            Comments ({blog.comments?.length || 0})
                        </h3>
                    </div>

                    {/* Comment Form */}
                    <form onSubmit={handleCommentSubmit} className="mb-12">
                        <div className="bg-gray-50 rounded-[2rem] p-6 focus-within:ring-2 focus-within:ring-[#E31E24] transition-all border border-gray-100 shadow-inner">
                            <textarea
                                rows="3"
                                value={commentText}
                                onChange={(e) => setCommentText(e.target.value)}
                                placeholder={user ? "Share your thoughts..." : "Please sign in to join the conversation"}
                                disabled={!user || isSubmittingComment}
                                className="w-full bg-transparent border-none focus:ring-0 text-gray-700 placeholder:text-gray-400 text-lg resize-none"
                            />
                            <div className="flex justify-end mt-4">
                                <button
                                    type="submit"
                                    disabled={!user || !commentText.trim() || isSubmittingComment}
                                    className="bg-[#E31E24] text-white px-8 py-3 rounded-xl font-bold disabled:opacity-50 hover:bg-red-700 transition-all shadow-lg flex items-center gap-2"
                                >
                                    {isSubmittingComment ? (
                                        <>
                                            <ArrowPathIcon className="h-5 w-5 animate-spin" />
                                            Posting...
                                        </>
                                    ) : (
                                        'Post Comment'
                                    )}
                                </button>
                            </div>
                        </div>
                        {!user && (
                            <p className="mt-4 text-center text-sm text-gray-500">
                                <Link to="/login" className="text-[#E31E24] font-bold hover:underline">Sign in</Link> to post a comment
                            </p>
                        )}
                    </form>

                    {/* Comments List */}
                    <div className="space-y-8">
                        {blog.comments && blog.comments.length > 0 ? (
                            blog.comments.map((comment, index) => (
                                <motion.div
                                    key={comment.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.05 }}
                                    className="flex gap-4 p-6 rounded-3xl bg-white border border-gray-100 shadow-sm group"
                                >
                                    <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center text-[#E31E24] font-bold text-lg flex-shrink-0">
                                        {comment.user_name?.charAt(0).toUpperCase() || 'U'}
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex items-center justify-between mb-2">
                                            <div>
                                                <h4 className="font-bold text-gray-900">{comment.user_name}</h4>
                                                <p className="text-xs text-gray-400 font-medium">
                                                    {new Date(comment.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                                </p>
                                            </div>
                                            {(user?.id === comment.user_id || user?.role === 'admin') && (
                                                <button
                                                    onClick={() => handleDeleteComment(comment.id)}
                                                    className={`p-2 rounded-xl transition-all ${user?.role === 'admin'
                                                            ? 'text-red-500 bg-red-50 opacity-100'
                                                            : 'text-gray-300 hover:text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100'
                                                        }`}
                                                    title="Delete Comment"
                                                >
                                                    <TrashIcon className="h-5 w-5" />
                                                </button>
                                            )}
                                        </div>
                                        <p className="text-gray-700 leading-relaxed text-lg">
                                            {comment.text}
                                        </p>
                                    </div>
                                </motion.div>
                            ))
                        ) : (
                            <div className="text-center py-16 bg-gray-50 rounded-[2rem] border-2 border-dashed border-gray-200">
                                <ChatBubbleLeftIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                                <p className="text-gray-500 font-medium">No comments yet. Be the first to start the discussion!</p>
                            </div>
                        )}
                    </div>
                </section>
            </div>
        </div>
    );
};

export default BlogPostDetail;
